type Project = {
    id: number;
    name: string;
    locality: string;
    country: string;
    budget: number;
    total_generation_kw: number;
    total_storage_capacity_kwh: number;
    generation_health?: HealthStatus;
    storage_health?: HealthStatus;
    sites: null | Site[];
    documents: null | Document[];
}

type GenerationSummary = {
    [key: string]: {
        [key: string]: number;
    };
}

type ProjectWithGenerationSummary = {
    instance: Project;
    results: GenerationSummary;
}

type ProjectListWithGenerationSummary = {
    instances: Project[];
    results: GenerationSummary;
}

type HealthStatus = 'normal' | 'system-fault' | 'no-data' | 'under-construction' | 'manual';

type StatusFilter = {
    [key in HealthStatus | 'not-on-map']: boolean;
};

type Installer = {
    id: number;
    email: string;
    name: string;
};

type Site = {
    id: number;
    active: boolean;
    name: string;
    address: string;
    latitude: number;
    longitude: number;
    devices: string[]; // Device IDs
    health_status: HealthStatus;
    manufacturer: string;
    installer: Installer | null;
    project: Project;
    generation_kw: number;
    storage_capacity_kwh: number;
    timezone: string;
};

type Alarm = {
    id: number;
    external_id: string;
    device: string;
    description: string;
    started_at: string;
    cleared_at: string;
    active: boolean;
}

type Schedule = {
    id: number;
    name: string;
    crontab: {
        id: number;
        minute: string;
        hour: string;
        human_readable: string;
    };
}

type SiteWithGenerationSummary = {
    instance: Site;
    schedule: Schedule;
    alarms: Alarm[];
    results: GenerationSummary;
}

type Instance = Site | Project;
type InstanceWithGenerationSummary = SiteWithGenerationSummary | ProjectWithGenerationSummary;

type Organisation = {
    id: number;
    name: string;
};

type User = {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_staff: boolean;
    is_superuser: boolean;
    date_joined: string;
    last_login: string | null;
    organisation: Organisation | null;
};

type ExternalAuthorisation = {
    id: number;
    user: User;
    external_account: string;
    external_id: string;
    external_token_id: string;
    manufacturer: string;
    type: 'Bearer' | 'Token';
}

type Document = {
    id: number;
    name: string;
    file: string;
    file_type: string;
    file_size: number;
    project: number;
    uploaded_by: number;
    uploaded_by_name: string;
    uploaded_at: string;
    file_url: string;
}

type PacificCountry = {
    code: string;
    name: string;
    flag: string;
}

const PACIFIC_COUNTRIES: PacificCountry[] = [
    { code: 'AS', name: 'American Samoa', flag: '🇦🇸' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺' },
    { code: 'CK', name: 'Cook Islands', flag: '🇨🇰' },
    { code: 'FJ', name: 'Fiji', flag: '🇫🇯' },
    { code: 'PF', name: 'French Polynesia', flag: '🇵🇫' },
    { code: 'GU', name: 'Guam', flag: '🇬🇺' },
    { code: 'KI', name: 'Kiribati', flag: '🇰🇮' },
    { code: 'MH', name: 'Marshall Islands', flag: '🇲🇭' },
    { code: 'FM', name: 'Micronesia', flag: '🇫🇲' },
    { code: 'NC', name: 'New Caledonia', flag: '🇳🇨' },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
    { code: 'NU', name: 'Niue', flag: '🇳🇺' },
    { code: 'NF', name: 'Norfolk Island', flag: '🇳🇫' },
    { code: 'MP', name: 'Northern Mariana Islands', flag: '🇲🇵' },
    { code: 'PW', name: 'Palau', flag: '🇵🇼' },
    { code: 'PG', name: 'Papua New Guinea', flag: '🇵🇬' },
    { code: 'PN', name: 'Pitcairn Islands', flag: '🇵🇳' },
    { code: 'WS', name: 'Samoa', flag: '🇼🇸' },
    { code: 'SB', name: 'Solomon Islands', flag: '🇸🇧' },
    { code: 'TK', name: 'Tokelau', flag: '🇹🇰' },
    { code: 'TO', name: 'Tonga', flag: '🇹🇴' },
    { code: 'TV', name: 'Tuvalu', flag: '🇹🇻' },
    { code: 'VU', name: 'Vanuatu', flag: '🇻🇺' },
    { code: 'WF', name: 'Wallis and Futuna', flag: '🇼🇫' }
];

export { PACIFIC_COUNTRIES };
export type { Project, HealthStatus, StatusFilter, Site, Installer, Alarm, PacificCountry, Instance, User, Organisation, ExternalAuthorisation, Document, ProjectWithGenerationSummary, ProjectListWithGenerationSummary, SiteWithGenerationSummary, InstanceWithGenerationSummary, Schedule };
